import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import cx from "clsx"
import type { ProgramConfig, UpdateProgramConfig } from "shared"
import { fetchProgramConfig, updateProgramConfig } from "../api"
import { AdminButton } from "@rawa/ui/AdminButton"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { NOT_AUTHORIZED_MESSAGE } from "../utils/notAuthorized"
import { isForbidden } from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./ProgramConfigPage.module.scss"

// A row's count as edited, as text, so an admin can clear the field while typing
// without it snapping back to a number. Parsed back to an integer on save.
type Edits = Record<string, string>

// The order toggle needs no text dance like the count does — a
// checkbox has no invalid intermediate state — so it's kept as its own map
// of booleans, separate from the count edits above.
type OrderEdits = Record<string, boolean>

type Update = { program: ProgramConfig; body: UpdateProgramConfig }

function isValidCount(value: string): boolean {
  return /^[1-9][0-9]*$/.test(value.trim())
}

function without<T>(edits: Record<string, T>, id: string) {
  const rest = { ...edits }
  delete rest[id]
  return rest
}

export function ProgramConfigPage() {
  const queryClient = useQueryClient()
  // Only rows the admin has edited have an entry; the rest show what loaded.
  const [edits, setEdits] = useState<Edits>({})
  const [orderEdits, setOrderEdits] = useState<OrderEdits>({})
  const [errorById, setErrorById] = useState<Record<string, string>>({})

  // Read each time the page is shown and never in the background.
  const {
    data: programs = [],
    isPending: loading,
    isError,
    error,
  } = useQuery({
    queryKey: ["program-config"],
    queryFn: fetchProgramConfig,
    staleTime: Infinity,
    refetchOnMount: "always",
    // The client's default retries exist for a kiosk tablet waking its Wi-Fi.
    // An administrator at a desk is told what went wrong instead.
    retry: false,
    meta: { source: "fetchProgramConfig", forbiddenIsExpected: true },
  })
  const forbidden = isError && isForbidden(error)
  const loadError = isError && !forbidden

  const countOf = (program: ProgramConfig) =>
    edits[program.id] ?? String(program.randomQuestionCount)
  const orderOf = (program: ProgramConfig) =>
    orderEdits[program.id] ?? program.programQuestionsFirst

  const update = useMutation({
    mutationFn: ({ program, body }: Update) =>
      updateProgramConfig(program.id, body),
    onError: (err, { program }) => {
      trackError(err, { source: "updateProgramConfig" })
      setErrorById((prev) => ({
        ...prev,
        [program.id]: `Couldn't save ${program.name}. Please try again.`,
      }))
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<ProgramConfig[]>(["program-config"], (held) =>
        held?.map((p) => (p.id === updated.id ? updated : p)),
      )
      setEdits((prev) => without(prev, updated.id))
      setOrderEdits((prev) => without(prev, updated.id))
    },
  })
  const savingId = update.isPending ? update.variables.program.id : null

  function save(program: ProgramConfig) {
    const value = countOf(program)
    if (!isValidCount(value)) {
      setErrorById((prev) => ({
        ...prev,
        [program.id]: "Enter a whole number of 1 or more.",
      }))
      return
    }
    setErrorById((prev) => ({ ...prev, [program.id]: "" }))
    update.mutate({
      program,
      body: {
        randomQuestionCount: Number(value),
        programQuestionsFirst: orderOf(program),
      },
    })
  }

  return (
    <div className={styles.page}>
      <div className={styles.titleBlock}>
        <h1 className={cx(typography.h2, styles.heading)}>Program config</h1>
        <p className={styles.subtitle}>
          Set how many pool questions each program asks per response, and
          whether they come before or after the standard ratings. When a program
          has fewer questions than the count, every one of them is shown
          instead.
        </p>
      </div>

      {!loading && forbidden && (
        <ErrorCallout>{NOT_AUTHORIZED_MESSAGE}</ErrorCallout>
      )}

      {loading && (
        <div
          role="status"
          aria-label="Loading program config"
          className={styles.panel}
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.row}>
              <span className={styles.skeletonName} />
              <span className={styles.skeletonInput} />
            </div>
          ))}
        </div>
      )}

      {!loading && loadError && (
        <ErrorCallout>Failed to load program config.</ErrorCallout>
      )}

      {!loading && !loadError && !forbidden && programs.length === 0 && (
        <p className={styles.empty}>No programs.</p>
      )}

      {!loading && !loadError && programs.length > 0 && (
        <div className={styles.panel}>
          <ul aria-label="Program config" className={styles.list}>
            {programs.map((program) => {
              const value = countOf(program)
              const questionsFirst = orderOf(program)
              const dirty =
                value !== String(program.randomQuestionCount) ||
                questionsFirst !== program.programQuestionsFirst
              const error = errorById[program.id]
              return (
                <li key={program.id} className={styles.row}>
                  <span className={styles.name}>{program.name}</span>
                  <div className={styles.control}>
                    {error && (
                      <span role="alert" className={styles.fieldError}>
                        {error}
                      </span>
                    )}
                    <label
                      className={styles.countLabel}
                      htmlFor={`random-question-count-${program.id}`}
                    >
                      Questions
                    </label>
                    <input
                      id={`random-question-count-${program.id}`}
                      className={styles.countInput}
                      type="number"
                      inputMode="numeric"
                      min={1}
                      step={1}
                      value={value}
                      disabled={savingId === program.id}
                      aria-label={`${program.name} random question count`}
                      aria-invalid={!!error}
                      onChange={(e) =>
                        setEdits((prev) => ({
                          ...prev,
                          [program.id]: e.target.value,
                        }))
                      }
                    />
                    <label className={styles.orderLabel}>
                      <input
                        type="checkbox"
                        checked={questionsFirst}
                        disabled={savingId === program.id}
                        aria-label={`${program.name} program questions first`}
                        onChange={(e) =>
                          setOrderEdits((prev) => ({
                            ...prev,
                            [program.id]: e.target.checked,
                          }))
                        }
                      />
                      Program questions first
                    </label>
                    <AdminButton
                      onClick={() => save(program)}
                      disabled={!dirty || savingId === program.id}
                    >
                      {savingId === program.id ? "Saving…" : "Save"}
                    </AdminButton>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
